import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AppstoreOutlined,
  BarcodeOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CreditCardOutlined,
  DeleteOutlined,
  EditOutlined,
  ExperimentOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  InboxOutlined,
  MedicineBoxOutlined,
  PlusOutlined,
  SearchOutlined,
  ShoppingOutlined,
  SyncOutlined,
  TagOutlined
} from "@ant-design/icons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert,
  Button,
  Input,
  Modal,
  Spin,
  Splitter,
  Switch,
  Table,
  Tooltip,
  Tree,
  message
} from "antd";
import GridSettings, { applyGridSettings, loadGridSettings } from "@controls/grid-settings";
import ComboFilter from "@controls/combo-filter";
import { hasLicensedModule, PROGRAM_MODULES } from "@core/module-license";
import { deleteSprTov, fetchSprTovList, fetchSprTovRestTotals } from "./spr-tov-api";
import SprTovDrawer from "./spr-tov-drawer";
import "./spr-tov.css";

const ROOT_UPPER = 0;
const GROUPS_WIDTH_KEY = "planOnline.sprTov.groupsWidth";
const EXPANDED_KEYS_KEY = "planOnline.sprTov.expandedKeys";
const SELECTED_GROUP_KEY = "planOnline.sprTov.selectedGroupId";
const SHOW_DELETED_KEY = "planOnline.sprTov.showDeleted";
const VID_PLAN_FILTER_KEY = "planOnline.sprTov.vidPlanFilter";
const USE_REST_FILTER_KEY = "planOnline.sprTov.useRestFilter";
const GRID_STORAGE_KEY = "planOnline.sprTov.gridSettings";
const GROUPS_WIDTH_DEFAULT = 280;
const GROUPS_WIDTH_MIN = 180;
const GROUPS_WIDTH_MAX = 560;

const loadGroupsWidth = () => {
  try {
    const n = Number(localStorage.getItem(GROUPS_WIDTH_KEY));
    if (!Number.isFinite(n)) return GROUPS_WIDTH_DEFAULT;
    return Math.min(GROUPS_WIDTH_MAX, Math.max(GROUPS_WIDTH_MIN, Math.round(n)));
  } catch {
    return GROUPS_WIDTH_DEFAULT;
  }
};

const loadStoredNumber = (key, fallback = null) => {
  try {
    const n = Number(localStorage.getItem(key));
    return Number.isFinite(n) ? n : fallback;
  } catch {
    return fallback;
  }
};

const saveStoredNumber = (key, value) => {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, String(value));
  } catch {
    /* ignore */
  }
};

const loadExpandedKeys = () => {
  try {
    const raw = localStorage.getItem(EXPANDED_KEYS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(String).filter((k) => k && k !== "root");
  } catch {
    return [];
  }
};

const saveExpandedKeys = (keys) => {
  try {
    const next = (keys || []).map(String).filter((k) => k && k !== "root");
    localStorage.setItem(EXPANDED_KEYS_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
};

const loadShowDeleted = () => {
  try {
    return localStorage.getItem(SHOW_DELETED_KEY) === "1";
  } catch {
    return false;
  }
};

const saveShowDeleted = (value) => {
  try {
    localStorage.setItem(SHOW_DELETED_KEY, value ? "1" : "0");
  } catch {
    /* ignore */
  }
};

const collectGroupKeys = (nodes, acc = new Set()) => {
  (nodes || []).forEach((node) => {
    if (node?.key != null && node.key !== "root") acc.add(String(node.key));
    if (node?.children?.length) collectGroupKeys(node.children, acc);
  });
  return acc;
};

const VID_PLAN_LABELS = {
  0: "Не планировать",
  1: "Каждый день",
  2: "Через день",
  7: "Раз в неделю",
  100: "По времени"
};

const USE_REST_LABELS = {
  0: "Услуга",
  1: "Товар",
  2: "Комплект",
  4: "Исследования"
};

const USE_REST_META = {
  0: { icon: MedicineBoxOutlined, kind: "service" },
  1: { icon: ShoppingOutlined, kind: "goods" },
  2: { icon: AppstoreOutlined, kind: "kit" },
  4: { icon: ExperimentOutlined, kind: "lab" }
};

const useRestKind = (useRest) => USE_REST_META[useRest]?.kind ?? "other";

const pluralize = (n, one, few, many) => {
  const mod100 = Math.abs(n) % 100;
  const mod10 = mod100 % 10;
  if (mod100 >= 11 && mod100 <= 19) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const pick = (row, ...keys) => {
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null) return row[key];
  }
  return undefined;
};

const toIsGroup = (value) => {
  if (value === true) return true;
  const n = Number(value);
  return Number.isFinite(n) && n === 1;
};

const normalizeRow = (row) => ({
  idTov: toNumber(pick(row, "idTov", "IdTov")),
  idUpper: toNumber(pick(row, "idUpper", "IdUpper")) ?? 0,
  isGroup: toIsGroup(pick(row, "isGroup", "IsGroup")),
  name: pick(row, "name", "Name") ?? "",
  art: pick(row, "art", "Art") ?? "",
  funit: pick(row, "funit", "Funit") ?? "",
  vidPlan: toNumber(pick(row, "vidPlan", "VidPlan")),
  zenaExp: toNumber(pick(row, "zenaExp", "ZenaExp")),
  isHide: Number(pick(row, "isHide", "IsHide")) === 1,
  prcZpl: toNumber(pick(row, "prcZpl", "PrcZpl")),
  depno: toNumber(pick(row, "depno", "Depno")),
  useRest: toNumber(pick(row, "useRest", "UseRest")),
  shk: pick(row, "shk", "Shk") ?? "",
  sync: Number(pick(row, "sync", "Sync")) === 1,
  isPlat: Number(pick(row, "isPlat", "IsPlat")) === 1,
  mark: Number(pick(row, "mark", "Mark")) === 1,
  labEnabled: Number(pick(row, "labEnabled", "LabEnabled")) === 1,
  gName: pick(row, "gName", "GName") ?? "",
  childCount: toNumber(pick(row, "childCount", "ChildCount")) ?? 0
});

const resolveParentId = (idUpper, idSet) => {
  if (idUpper == null || idUpper === ROOT_UPPER) return ROOT_UPPER;
  if (!idSet.has(idUpper)) return ROOT_UPPER;
  return idUpper;
};

/** Дерево только групп (IS_GROUP = 1). */
export const buildGroupTree = (rows) => {
  const list = (Array.isArray(rows) ? rows : [])
    .map(normalizeRow)
    .filter((r) => r.idTov != null && r.isGroup);
  const idSet = new Set(list.map((r) => r.idTov));
  const byParent = new Map();

  list.forEach((row) => {
    const parent = resolveParentId(row.idUpper, idSet);
    let bucket = byParent.get(parent);
    if (!bucket) {
      bucket = [];
      byParent.set(parent, bucket);
    }
    bucket.push(row);
  });

  const build = (parentId, stack = new Set()) =>
    (byParent.get(parentId) || []).map((row) => {
      if (stack.has(row.idTov)) {
        return {
          key: String(row.idTov),
          idTov: row.idTov,
          title: row.name,
          name: row.name,
          childCount: row.childCount,
          isLeaf: true
        };
      }
      stack.add(row.idTov);
      const children = build(row.idTov, stack);
      stack.delete(row.idTov);
      return {
        key: String(row.idTov),
        idTov: row.idTov,
        title: row.name,
        name: row.name,
        childCount: row.childCount,
        isLeaf: !children.length,
        ...(children.length ? { children } : {})
      };
    });

  return build(ROOT_UPPER);
};

const flattenParentOptions = (nodes, depth = 0, acc = []) => {
  (nodes || []).forEach((node) => {
    const pad = depth > 0 ? `${"\u00A0".repeat(depth * 3)}` : "";
    acc.push({ value: node.idTov, label: `${pad}${node.name || `№${node.idTov}`}` });
    if (node.children?.length) flattenParentOptions(node.children, depth + 1, acc);
  });
  return acc;
};

const collectExpandableKeys = (nodes, acc = []) => {
  (nodes || []).forEach((node) => {
    if (node.children?.length) {
      acc.push(node.key);
      collectExpandableKeys(node.children, acc);
    }
  });
  return acc;
};

const formatMoney = (value) => {
  const n = toNumber(value);
  if (n == null) return "";
  return n.toLocaleString("ru-RU", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const SprTov = () => {
  const navigate = useNavigate();
  const license = useSelector((state) => state.license.data);
  const hasWarehouse = hasLicensedModule(license, PROGRAM_MODULES.WAREHOUSE);
  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = routeId === "new";
  const editId = routeId && /^\d+$/.test(routeId) ? Number(routeId) : null;
  const drawerOpen = isNew || editId != null;
  const newIdUpper = toNumber(searchParams.get("upper")) ?? 0;
  const isGroupNew = searchParams.get("group") === "1";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState(
    () => loadStoredNumber(SELECTED_GROUP_KEY, ROOT_UPPER) ?? ROOT_UPPER
  );
  const [expandedKeys, setExpandedKeys] = useState(() => loadExpandedKeys());
  const [groupFilter, setGroupFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [vidPlanFilter, setVidPlanFilter] = useState(() => loadStoredNumber(VID_PLAN_FILTER_KEY));
  const [useRestFilter, setUseRestFilter] = useState(() => loadStoredNumber(USE_REST_FILTER_KEY));
  const [showDeleted, setShowDeleted] = useState(loadShowDeleted);
  const [tableScrollY, setTableScrollY] = useState(400);
  const [tableScrollX, setTableScrollX] = useState(600);
  const [groupsWidth, setGroupsWidth] = useState(loadGroupsWidth);
  const [gridSettings, setGridSettings] = useState(() => loadGridSettings(GRID_STORAGE_KEY));
  const [restTotals, setRestTotals] = useState(() => new Map());
  const leavesPaneRef = useRef(null);
  const uiRestoredRef = useRef(false);
  const prevGroupFilterRef = useRef("");
  const requestIdRef = useRef(0);

  useEffect(() => {
    const el = leavesPaneRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const update = () => {
      const h = el.clientHeight;
      // высота тела таблицы ≈ контейнер минус шапка (~40px)
      setTableScrollY(Math.max(160, h - 40));
      setTableScrollX(Math.max(320, el.clientWidth));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [rows.length]);

  const loadItems = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError("");
    fetchSprTovList()
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        setRows(Array.isArray(data) ? data : []);
      })
      .catch((e) => {
        if (requestId !== requestIdRef.current) return;
        setError(e.message || "Не удалось загрузить услуги");
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
    fetchSprTovRestTotals()
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        const next = new Map();
        (Array.isArray(data) ? data : []).forEach((row) => {
          const rowIdTov = toNumber(pick(row, "idTov", "IdTov"));
          if (rowIdTov != null) next.set(rowIdTov, toNumber(pick(row, "kol", "Kol")) ?? 0);
        });
        setRestTotals(next);
      })
      .catch(() => {
        if (requestId === requestIdRef.current) setRestTotals(new Map());
      });
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const normalized = useMemo(
    () => (Array.isArray(rows) ? rows : []).map(normalizeRow).filter((r) => r.idTov != null),
    [rows]
  );

  const groupTree = useMemo(() => buildGroupTree(normalized), [normalized]);
  const parentOptions = useMemo(() => flattenParentOptions(groupTree), [groupTree]);

  const filterQ = nameFilter.trim().toLowerCase();
  const groupFilterQ = groupFilter.trim().toLowerCase();

  const leafMatchesFilter = useCallback(
    (leaf) => {
      if (!filterQ) return true;
      const fields = [
        leaf.name,
        leaf.art,
        leaf.gName,
        VID_PLAN_LABELS[leaf.vidPlan] ?? (leaf.vidPlan == null ? "" : String(leaf.vidPlan)),
        USE_REST_LABELS[leaf.useRest] ?? (leaf.useRest == null ? "" : String(leaf.useRest))
      ];
      return fields.some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(filterQ)
      );
    },
    [filterQ]
  );

  /** Поиск группы по части имени — совпадения и предки. */
  const { tree: filteredGroupTree, expandKeys: filterExpandKeys } = useMemo(() => {
    if (!groupFilterQ) return { tree: groupTree, expandKeys: null };

    const expandKeys = [];
    const walk = (list) => {
      const out = [];
      (list || []).forEach((node) => {
        const childResult = walk(node.children || []);
        const nameMatch = String(node.name || "")
          .toLowerCase()
          .includes(groupFilterQ);
        if (nameMatch || childResult.length) {
          const next = { ...node };
          if (childResult.length) {
            next.children = childResult;
            next.isLeaf = false;
            expandKeys.push(node.key);
          } else {
            delete next.children;
            next.isLeaf = true;
          }
          out.push(next);
        }
      });
      return out;
    };

    return { tree: walk(groupTree), expandKeys };
  }, [groupTree, groupFilterQ]);

  /** Восстановить раскрытие и выбранную группу после загрузки данных. */
  useEffect(() => {
    if (loading || !normalized.length || !groupTree.length || uiRestoredRef.current) return;
    uiRestoredRef.current = true;

    try {
      localStorage.removeItem("planOnline.sprTov.selectedServiceId");
    } catch {
      /* ignore */
    }

    const validGroupKeys = collectGroupKeys(groupTree);
    const byId = new Map(normalized.map((r) => [r.idTov, r]));
    const ancestorsOf = (id) => {
      const path = [];
      let cur = byId.get(id);
      for (let i = 0; i < 64 && cur && cur.idUpper > 0; i += 1) {
        path.push(String(cur.idUpper));
        cur = byId.get(cur.idUpper);
      }
      return path;
    };

    let nextGroupId = loadStoredNumber(SELECTED_GROUP_KEY, ROOT_UPPER) ?? ROOT_UPPER;
    if (nextGroupId > 0 && !validGroupKeys.has(String(nextGroupId))) {
      nextGroupId = ROOT_UPPER;
    }

    // Если открыт URL карточки — подстроить группу под неё (без сохранения услуги).
    if (routeId && /^\d+$/.test(routeId)) {
      const row = normalized.find((r) => r.idTov === Number(routeId));
      if (row?.isGroup) {
        nextGroupId = row.idTov;
      } else if (row && !row.isGroup) {
        nextGroupId =
          row.idUpper > 0 && validGroupKeys.has(String(row.idUpper)) ? row.idUpper : ROOT_UPPER;
      }
    }

    const savedExpanded = loadExpandedKeys().filter((k) => validGroupKeys.has(k));
    const ensurePath =
      nextGroupId > 0 ? ancestorsOf(nextGroupId).filter((k) => validGroupKeys.has(k)) : [];
    const nextExpanded = [
      ...new Set([
        ...(savedExpanded.length
          ? savedExpanded
          : groupTree.filter((node) => node.children?.length).map((node) => node.key)),
        ...ensurePath
      ])
    ];
    setExpandedKeys(nextExpanded);
    saveExpandedKeys(nextExpanded);

    setSelectedGroupId(nextGroupId);
    saveStoredNumber(SELECTED_GROUP_KEY, nextGroupId);
  }, [loading, normalized, groupTree, routeId]);

  /** Поиск по группам временно раскрывает путь; при сбросе — сохранённая вложенность. */
  useEffect(() => {
    if (!uiRestoredRef.current || !groupTree.length) return;
    const prev = prevGroupFilterRef.current;
    prevGroupFilterRef.current = groupFilterQ;

    if (groupFilterQ) {
      setExpandedKeys(filterExpandKeys || collectExpandableKeys(filteredGroupTree));
      return;
    }
    if (prev) {
      const validGroupKeys = collectGroupKeys(groupTree);
      const savedExpanded = loadExpandedKeys().filter((k) => validGroupKeys.has(k));
      setExpandedKeys(
        savedExpanded.length
          ? savedExpanded
          : groupTree.filter((node) => node.children?.length).map((node) => node.key)
      );
    }
  }, [groupFilterQ, groupTree, filteredGroupTree, filterExpandKeys]);

  useEffect(() => {
    saveStoredNumber(SELECTED_GROUP_KEY, selectedGroupId);
  }, [selectedGroupId]);

  /** После обновления списка убрать из раскрытия несуществующие группы. */
  useEffect(() => {
    if (!uiRestoredRef.current || groupFilterQ || !groupTree.length) return;
    const valid = collectGroupKeys(groupTree);
    setExpandedKeys((prev) => {
      const next = prev.filter((k) => valid.has(String(k)));
      return next.length === prev.length ? prev : next;
    });
  }, [groupTree, groupFilterQ]);

  const selectedGroup = useMemo(() => {
    if (selectedGroupId <= 0) return null;
    return normalized.find((r) => r.idTov === selectedGroupId && r.isGroup) || null;
  }, [normalized, selectedGroupId]);

  const selectedGroupChildCount = useMemo(() => {
    if (selectedGroupId <= 0) return 0;
    return normalized.reduce((n, r) => (r.idUpper === selectedGroupId ? n + 1 : n), 0);
  }, [normalized, selectedGroupId]);

  /** Прямое число услуг в каждой группе (видимые, без самих групп). */
  const leafCountByGroup = useMemo(() => {
    const map = new Map();
    normalized.forEach((r) => {
      if (r.isGroup || r.isHide) return;
      const key = r.idUpper > 0 ? r.idUpper : ROOT_UPPER;
      map.set(key, (map.get(key) ?? 0) + 1);
    });
    return map;
  }, [normalized]);

  const leafTotalCount = useMemo(
    () => normalized.reduce((n, r) => (!r.isGroup && !r.isHide ? n + 1 : n), 0),
    [normalized]
  );

  const canEditGroup = Boolean(selectedGroup);
  // Как в Delphi: нельзя удалить группу, если есть любые дочерние (подгруппы или услуги).
  const canDeleteGroup = canEditGroup && selectedGroupChildCount === 0;

  const comboKinds = useMemo(
    () => [
      {
        key: "vidPlan",
        label: "Планирование",
        width: 170,
        options: Object.entries(VID_PLAN_LABELS).map(([v, label]) => ({
          value: Number(v),
          label
        }))
      },
      {
        key: "useRest",
        label: "Вид",
        width: 150,
        options: Object.entries(USE_REST_LABELS).map(([v, label]) => ({
          value: Number(v),
          label
        }))
      }
    ],
    []
  );

  const comboValue = useMemo(
    () => ({ q: nameFilter, vidPlan: vidPlanFilter, useRest: useRestFilter }),
    [nameFilter, vidPlanFilter, useRestFilter]
  );

  const onComboChange = useCallback((patch) => {
    if (Object.prototype.hasOwnProperty.call(patch, "q")) setNameFilter(patch.q || "");
    if (Object.prototype.hasOwnProperty.call(patch, "vidPlan")) {
      const next = patch.vidPlan == null || patch.vidPlan === "" ? null : toNumber(patch.vidPlan);
      setVidPlanFilter(next);
      saveStoredNumber(VID_PLAN_FILTER_KEY, next);
    }
    if (Object.prototype.hasOwnProperty.call(patch, "useRest")) {
      const next = patch.useRest == null || patch.useRest === "" ? null : toNumber(patch.useRest);
      setUseRestFilter(next);
      saveStoredNumber(USE_REST_FILTER_KEY, next);
    }
  }, []);

  const leaves = useMemo(() => {
    const list = normalized.filter((r) => !r.isGroup);
    const inGroup =
      selectedGroupId === ROOT_UPPER
        ? list
        : list.filter((r) => r.idUpper === selectedGroupId);
    const visible = showDeleted ? inGroup : inGroup.filter((r) => !r.isHide);
    return visible.filter(
      (r) =>
        leafMatchesFilter(r) &&
        (vidPlanFilter == null || (r.vidPlan ?? 0) === vidPlanFilter) &&
        (useRestFilter == null || (r.useRest ?? 0) === useRestFilter)
    );
  }, [normalized, selectedGroupId, leafMatchesFilter, showDeleted, vidPlanFilter, useRestFilter]);

  const hasActiveFilter = Boolean(filterQ) || vidPlanFilter != null || useRestFilter != null;

  useEffect(() => {
    if (editId == null || loading || !normalized.length) return;
    const row = normalized.find((r) => r.idTov === editId);
    if (!row) {
      navigate("/spr/tov", { replace: true });
      return;
    }
    if (!row.isGroup) {
      const parentId = row.idUpper > 0 ? row.idUpper : ROOT_UPPER;
      setSelectedGroupId((prev) => (prev === parentId ? prev : parentId));
      saveStoredNumber(SELECTED_GROUP_KEY, parentId);
    } else {
      setSelectedGroupId(row.idTov);
      saveStoredNumber(SELECTED_GROUP_KEY, row.idTov);
    }
  }, [editId, normalized, loading, navigate]);

  const openEdit = useCallback(
    (idTov) => {
      if (idTov == null) return;
      navigate(`/spr/tov/${idTov}`);
    },
    [navigate]
  );

  const onGroupExpand = useCallback(
    (keys) => {
      const next = (keys || []).filter((k) => k !== "root").map(String);
      setExpandedKeys(next);
      if (!groupFilter.trim()) saveExpandedKeys(next);
    },
    [groupFilter]
  );

  const openCreateService = useCallback(() => {
    const upper = selectedGroupId > 0 ? selectedGroupId : 0;
    navigate(`/spr/tov/new?upper=${upper}`);
  }, [navigate, selectedGroupId]);

  const openCreateGroup = useCallback(() => {
    const upper = selectedGroupId > 0 ? selectedGroupId : 0;
    navigate(`/spr/tov/new?group=1&upper=${upper}`);
  }, [navigate, selectedGroupId]);

  const onDelete = useCallback(
    (record) => {
      if (record?.idTov == null) return;
      const hasChildren = normalized.some((r) => r.idUpper === record.idTov);
      if (hasChildren || (record.childCount ?? 0) > 0 || record.children?.length) {
        message.warning("Нельзя удалить: есть вложенные элементы");
        return;
      }

      Modal.confirm({
        title: "Удалить?",
        content: record.name || `№${record.idTov}`,
        okText: "Удалить",
        cancelText: "Отмена",
        okButtonProps: { danger: true },
        onOk: () =>
          deleteSprTov(record.idTov)
            .then(() => {
              message.success("Удалено");
              if (editId === record.idTov) navigate("/spr/tov");
              if (selectedGroupId === record.idTov) setSelectedGroupId(ROOT_UPPER);
              loadItems();
            })
            .catch((e) => {
              message.error(e.message || "Не удалось удалить");
              return Promise.reject(e);
            })
      });
    },
    [editId, loadItems, navigate, normalized, selectedGroupId]
  );

  const closeDrawer = useCallback(() => {
    navigate("/spr/tov");
  }, [navigate]);

  const onSaved = useCallback(
    (saved) => {
      const savedId = toNumber(pick(saved || {}, "idTov", "IdTov"));
      if (saved && savedId != null) {
        setRows((prev) => {
          const idx = prev.findIndex((r) => toNumber(pick(r, "idTov", "IdTov")) === savedId);
          return idx < 0
            ? [...prev, saved]
            : prev.map((r, i) => (i === idx ? { ...r, ...saved } : r));
        });
      } else {
        loadItems();
      }
      navigate("/spr/tov");
    },
    [loadItems, navigate]
  );

  const treeTitleRender = useCallback(
    (node) => {
      const isEditing = editId != null && node.idTov === editId;
      const isExpanded = node.key === "root" || expandedKeys.includes(String(node.key));
      const count =
        node.key === "root"
          ? leafTotalCount
          : leafCountByGroup.get(node.idTov) ?? 0;
      return (
        <span className={`spr-tov__group-node${isEditing ? " spr-tov__group-node--editing" : ""}`}>
          <span className="spr-tov__group-node-icon">
            {isExpanded ? <FolderOpenOutlined /> : <FolderOutlined />}
          </span>
          <span className="spr-tov__group-node-text" title={node.name}>
            {node.name}
          </span>
          {count > 0 ? <span className="spr-tov__group-node-count">{count}</span> : null}
        </span>
      );
    },
    [editId, expandedKeys, leafCountByGroup, leafTotalCount]
  );

  const allColumns = useMemo(
    () => [
      {
        title: "Наименование",
        dataIndex: "name",
        key: "name",
        render: (name, record) => {
          const isEditing = editId != null && record.idTov === editId;
          const meta = USE_REST_META[record.useRest];
          const Icon = meta?.icon ?? TagOutlined;
          const sub = [
            record.art ? `арт. ${record.art}` : "",
            selectedGroupId === ROOT_UPPER && record.gName ? record.gName : ""
          ]
            .filter(Boolean)
            .join(" · ");
          return (
            <span className={`spr-tov__name${isEditing ? " spr-tov__name--editing" : ""}`}>
              <Tooltip title={USE_REST_LABELS[record.useRest] ?? ""}>
                <span className="spr-tov__name-icon" data-kind={useRestKind(record.useRest)}>
                  <Icon />
                </span>
              </Tooltip>
              <span className="spr-tov__name-block">
                <span className="spr-tov__name-line">
                  <span className="spr-tov__name-text" title={name}>{name}</span>
                  {record.isHide ? <span className="spr-tov__hidden-pill">Скрыта</span> : null}
                </span>
                {sub ? <span className="spr-tov__name-sub">{sub}</span> : null}
              </span>
              <span className="spr-tov__actions" onClick={(e) => e.stopPropagation()}>
                <Tooltip title="Удалить">
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    aria-label="Удалить"
                    onClick={() => onDelete(record)}
                  />
                </Tooltip>
              </span>
            </span>
          );
        }
      },
      {
        title: "План",
        dataIndex: "vidPlan",
        key: "vidPlan",
        width: 64,
        align: "center",
        render: (v) => {
          if (!v) return "";
          const label = VID_PLAN_LABELS[v] ?? String(v);
          const badge = [1, 2, 7].includes(v) ? v : VID_PLAN_LABELS[v] == null ? v : null;
          const Icon = v === 100 ? ClockCircleOutlined : CalendarOutlined;
          return (
            <Tooltip title={label}>
              <span className="spr-tov__plan-chip">
                <Icon />
                {badge != null ? <span className="spr-tov__plan-chip-badge">{badge}</span> : null}
              </span>
            </Tooltip>
          );
        }
      },
      {
        title: "Ед.",
        dataIndex: "funit",
        key: "funit",
        width: 60,
        ellipsis: true,
        render: (v) => <span className="spr-tov__unit">{v}</span>
      },
      {
        title: "Цена",
        dataIndex: "zenaExp",
        key: "zenaExp",
        width: 104,
        align: "right",
        render: (v) =>
          v == null || v === 0 ? (
            <span className="spr-tov__empty-dash">—</span>
          ) : (
            <span className="spr-tov__price">
              {formatMoney(v)}
              <span className="spr-tov__price-cur"> ₽</span>
            </span>
          )
      },
      ...(hasWarehouse
        ? [
            {
              title: "Остаток",
              dataIndex: "idTov",
              key: "rest",
              width: 96,
              align: "right",
              render: (idTov, record) => {
                if (record.isGroup || record.useRest !== 1) return "";
                const kol = restTotals.get(idTov) ?? 0;
                const kind = kol > 0 ? "pos" : kol < 0 ? "neg" : "zero";
                return (
                  <span
                    className="spr-tov__rest-chip"
                    data-kind={kind}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/warehouse/product-card?idTov=${idTov}`);
                    }}
                  >
                    {kol.toLocaleString("ru-RU", { maximumFractionDigits: 3 })}
                  </span>
                );
              }
            }
          ]
        : []),
      {
        title: "Признаки",
        key: "flags",
        width: 96,
        render: (v, record) => {
          const flags = [
            record.sync ? { icon: SyncOutlined, label: "Синхронизация" } : null,
            record.mark ? { icon: BarcodeOutlined, label: "Маркировка" } : null,
            record.labEnabled ? { icon: ExperimentOutlined, label: "Лаборатория" } : null,
            record.isPlat ? { icon: CreditCardOutlined, label: "Платная" } : null
          ].filter(Boolean);
          if (!flags.length) return "";
          return (
            <span className="spr-tov__flags">
              {flags.map(({ icon: FlagIcon, label }) => (
                <Tooltip key={label} title={label}>
                  <FlagIcon className="spr-tov__flag-icon" />
                </Tooltip>
              ))}
            </span>
          );
        }
      }
    ],
    [editId, onDelete, hasWarehouse, restTotals, navigate, selectedGroupId]
  );

  const columns = useMemo(
    () => applyGridSettings(allColumns, gridSettings),
    [allColumns, gridSettings]
  );

  const onGridSettingsChange = useCallback((next) => {
    setGridSettings(next);
  }, []);

  const selectedKeys =
    selectedGroupId > 0 ? [String(selectedGroupId)] : [];

  return (
    <div className="spr-tov">
      {error ? <Alert className="spr-tov__error" type="error" showIcon message={error} /> : null}
      <div className="spr-tov__body">
        <Splitter
          className="spr-tov__splitter"
          onResize={(sizes) => {
            const next = Number(sizes?.[0]);
            if (Number.isFinite(next)) setGroupsWidth(Math.round(next));
          }}
          onResizeEnd={(sizes) => {
            const next = Number(sizes?.[0]);
            if (!Number.isFinite(next)) return;
            const width = Math.min(
              GROUPS_WIDTH_MAX,
              Math.max(GROUPS_WIDTH_MIN, Math.round(next))
            );
            setGroupsWidth(width);
            try {
              localStorage.setItem(GROUPS_WIDTH_KEY, String(width));
            } catch {
              /* ignore */
            }
          }}
        >
          <Splitter.Panel
            size={groupsWidth}
            min={GROUPS_WIDTH_MIN}
            max={GROUPS_WIDTH_MAX}
            className="spr-tov__groups-panel"
          >
            <div className="spr-tov__groups">
              <div className="spr-tov__pane-title">Группы</div>
              <div className="spr-tov__pane-toolbar spr-tov__pane-toolbar--groups">
                <div className="spr-tov__extra-actions">
                  <Tooltip title="Добавить группу">
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      aria-label="Добавить группу"
                      onClick={openCreateGroup}
                    />
                  </Tooltip>
                  <Tooltip title="Изменить">
                    <span>
                      <Button
                        icon={<EditOutlined />}
                        aria-label="Изменить"
                        disabled={!canEditGroup}
                        onClick={() => openEdit(selectedGroupId)}
                      />
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      !canEditGroup
                        ? "Выберите группу"
                        : canDeleteGroup
                          ? "Удалить"
                          : "Нельзя удалить: есть вложенные элементы"
                    }
                  >
                    <span>
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        aria-label="Удалить"
                        disabled={!canDeleteGroup}
                        onClick={() => selectedGroup && onDelete(selectedGroup)}
                      />
                    </span>
                  </Tooltip>
                </div>
                <Input
                  allowClear
                  variant="borderless"
                  className="spr-tov__group-filter"
                  prefix={<SearchOutlined />}
                  placeholder="Поиск группы…"
                  value={groupFilter}
                  onChange={(e) => setGroupFilter(e.target.value)}
                />
              </div>
              <div className="spr-tov__groups-tree">
                {loading && !rows.length ? (
                  <div className="spr-tov__center">
                    <Spin tip="Загрузка..." />
                  </div>
                ) : (
                  <Tree
                    blockNode
                    treeData={[
                      {
                        key: "root",
                        idTov: ROOT_UPPER,
                        name: "Все услуги",
                        title: "Все услуги",
                        children: filteredGroupTree
                      }
                    ]}
                    titleRender={(node) => treeTitleRender(node)}
                    expandedKeys={["root", ...expandedKeys]}
                    onExpand={onGroupExpand}
                    selectedKeys={selectedGroupId === ROOT_UPPER ? ["root"] : selectedKeys}
                    onSelect={(keys) => {
                      const key = keys?.[0];
                      if (key == null || key === "root") {
                        setSelectedGroupId(ROOT_UPPER);
                        saveStoredNumber(SELECTED_GROUP_KEY, ROOT_UPPER);
                        return;
                      }
                      const id = Number(key);
                      const next = Number.isFinite(id) ? id : ROOT_UPPER;
                      setSelectedGroupId(next);
                      saveStoredNumber(SELECTED_GROUP_KEY, next);
                    }}
                  />
                )}
              </div>
            </div>
          </Splitter.Panel>
          <Splitter.Panel className="spr-tov__leaves-panel" min={320}>
            <div className="spr-tov__leaves">
              <div className="spr-tov__leaves-header">
                <span className="spr-tov__leaves-title">
                  {selectedGroup?.name || "Все услуги"}
                </span>
                <span className="spr-tov__leaves-count">
                  {leaves.length} {pluralize(leaves.length, "услуга", "услуги", "услуг")}
                </span>
              </div>
              <div className="spr-tov__pane-toolbar spr-tov__pane-toolbar--leaves">
                <div className="spr-tov__extra-actions">
                  <Tooltip title="Создать">
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      aria-label="Создать"
                      onClick={openCreateService}
                    />
                  </Tooltip>
                </div>
                <ComboFilter
                  className="spr-tov__combo"
                  value={comboValue}
                  kinds={comboKinds}
                  placeholder="Поиск…"
                  ariaLabel="Поиск и фильтры услуг"
                  onChange={onComboChange}
                />
                <span className="spr-tov__show-deleted">
                  <Switch
                    size="small"
                    checked={showDeleted}
                    onChange={(next) => {
                      setShowDeleted(next);
                      saveShowDeleted(next);
                    }}
                  />
                  <span className="spr-tov__show-deleted-label">Показывать удаленные</span>
                </span>
                <GridSettings
                  title="Настройка таблицы"
                  storageKey={GRID_STORAGE_KEY}
                  columns={allColumns}
                  value={gridSettings}
                  onChange={onGridSettingsChange}
                  buttonProps={{ className: "spr-tov__grid-settings-btn" }}
                />
              </div>
              <div className="spr-tov__leaves-table" ref={leavesPaneRef}>
                <Table
                  className="spr-tov__table"
                  size="small"
                  rowKey="idTov"
                  columns={columns}
                  dataSource={leaves}
                  loading={loading}
                  pagination={false}
                  virtual
                  onRow={(record) => ({
                    style: { cursor: "pointer" },
                    onClick: () => openEdit(record.idTov)
                  })}
                  rowClassName={(record) =>
                    [
                      "spr-tov__row",
                      record.isHide ? "spr-tov__row--hidden" : "",
                      editId != null && record.idTov === editId ? "spr-tov__row--editing" : ""
                    ]
                      .filter(Boolean)
                      .join(" ")
                  }
                  scroll={{ x: tableScrollX, y: tableScrollY }}
                  locale={{
                    emptyText: (
                      <span className="spr-tov__empty">
                        <InboxOutlined className="spr-tov__empty-icon" />
                        {hasActiveFilter
                          ? "Нет услуг по фильтру"
                          : selectedGroupId > 0
                            ? "Нет услуг в группе"
                            : "Нет услуг"}
                      </span>
                    )
                  }}
                />
              </div>
            </div>
          </Splitter.Panel>
        </Splitter>
      </div>
      <SprTovDrawer
        open={drawerOpen}
        idTov={editId}
        isNew={isNew}
        isGroupNew={isGroupNew}
        isGroup={Boolean(
          isGroupNew ||
            (editId != null && normalized.find((r) => r.idTov === editId)?.isGroup)
        )}
        idUpper={newIdUpper}
        parentOptions={parentOptions}
        onClose={closeDrawer}
        onSaved={onSaved}
      />
    </div>
  );
};

export default SprTov;
